/*
 * Vibee shared flash countdown
 * --------------------------------
 * Daily-midnight countdown shared by the homepage flash-deals section and the
 * PDP flash bar. It always counts down to the NEXT Europe/Istanbul midnight
 * (00:00:00), then rolls over to the following day without hiding or reloading.
 *
 * MARKET ASSUMPTION: Vibee is a single-market (Turkey) storefront, so the
 * countdown target is hard-anchored to Europe/Istanbul midnight regardless of
 * the visitor's own browser timezone. The Istanbul wall-clock is derived with
 * Intl.DateTimeFormat({ timeZone: 'Europe/Istanbul' }).formatToParts() — this
 * is robust across browsers (Safari included) and never depends on parsing a
 * localized date string back into a Date.
 *
 * Each countdown root: [data-vibee-flash-countdown]
 *   with child boxes [data-unit="hours"|"minutes"|"seconds"].
 * Optional [data-server-now] (epoch seconds) corrects client clock skew (PDP).
 *
 * Digit updates use span-swap micro-slide (see vibee-motion.css) — transform/
 * opacity only, and honors prefers-reduced-motion.
 */
(function () {
  if (window.__vibeeFlashCountdownInit) return;
  window.__vibeeFlashCountdownInit = true;

  var DAY_MS = 24 * 60 * 60 * 1000;
  var ISTANBUL_TZ = 'Europe/Istanbul';
  var SLIDE_MS = 200;

  // Cached Istanbul formatter (24h, numeric parts) — reused every tick.
  var istanbulParts = null;
  try {
    istanbulParts = new Intl.DateTimeFormat('en-GB', {
      timeZone: ISTANBUL_TZ,
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  } catch (e) {
    istanbulParts = null;
  }

  function pad2(value) {
    return String(value).padStart(2, '0');
  }

  // Returns Istanbul wall-clock {hour, minute, second} for a given epoch ms.
  function istanbulClock(epochMs) {
    var date = new Date(epochMs);
    if (istanbulParts) {
      var parts = istanbulParts.formatToParts(date);
      var map = {};
      for (var i = 0; i < parts.length; i += 1) {
        if (parts[i].type !== 'literal') map[parts[i].type] = parts[i].value;
      }
      var hour = parseInt(map.hour, 10);
      // Some engines emit "24" for midnight — normalize to 0.
      if (hour === 24 || isNaN(hour)) hour = 0;
      return {
        hour: hour,
        minute: parseInt(map.minute, 10) || 0,
        second: parseInt(map.second, 10) || 0
      };
    }
    // Fallback: assume the browser is already in Istanbul time.
    return { hour: date.getHours(), minute: date.getMinutes(), second: date.getSeconds() };
  }

  // Milliseconds remaining until the next Europe/Istanbul 00:00:00.
  // Always > 0 and <= DAY_MS; at exactly 00:00:00 it resets to a full day.
  function getNextIstanbulMidnight(epochMs) {
    var c = istanbulClock(epochMs);
    var secondsSinceMidnight = c.hour * 3600 + c.minute * 60 + c.second;
    var secondsUntil = 86400 - secondsSinceMidnight;
    if (secondsUntil <= 0) secondsUntil += 86400;
    var ms = secondsUntil * 1000 - (epochMs % 1000);
    if (ms <= 0) ms += DAY_MS;
    return ms;
  }

  function ensureCountdownBox(box) {
    if (box.classList.contains('m-countdown-box')) return box;

    var text = box.textContent.trim();
    box.textContent = '';
    box.classList.add('m-countdown-box');

    var stack = document.createElement('span');
    stack.className = 'm-countdown-box__stack';

    var digit = document.createElement('span');
    digit.className = 'm-countdown-box__digit is-active';
    digit.textContent = text || '--';
    digit.dataset.countdownDigit = '1';

    stack.appendChild(digit);
    box.appendChild(stack);
    return box;
  }

  function setDigit(box, nextValue) {
    ensureCountdownBox(box);
    var stack = box.querySelector('.m-countdown-box__stack');
    if (!stack) return;

    var current = stack.querySelector('.m-countdown-box__digit:not(.is-exiting)');
    if (!current) {
      current = document.createElement('span');
      current.className = 'm-countdown-box__digit is-active';
      stack.appendChild(current);
    }

    if (current.textContent === nextValue) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      current.textContent = nextValue;
      return;
    }

    current.classList.remove('is-active');
    current.classList.add('is-exiting');

    var incoming = document.createElement('span');
    incoming.className = 'm-countdown-box__digit is-entering';
    incoming.textContent = nextValue;
    stack.appendChild(incoming);

    requestAnimationFrame(function () {
      incoming.classList.add('is-active');
    });

    window.setTimeout(function () {
      if (current.parentNode) current.parentNode.removeChild(current);
      incoming.classList.remove('is-entering');
      incoming.classList.add('is-active');
    }, SLIDE_MS);
  }

  function paint(node, remainingMs) {
    var hoursEl = node.querySelector('[data-unit="hours"]');
    var minutesEl = node.querySelector('[data-unit="minutes"]');
    var secondsEl = node.querySelector('[data-unit="seconds"]');
    if (!hoursEl || !minutesEl || !secondsEl) return;

    var totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));
    setDigit(hoursEl, pad2(Math.floor(totalSeconds / 3600)));
    setDigit(minutesEl, pad2(Math.floor((totalSeconds % 3600) / 60)));
    setDigit(secondsEl, pad2(totalSeconds % 60));
  }

  function initNode(node) {
    // Clear any prior interval (e.g. Shopify section re-render in the editor).
    if (node.__vibeeCountdownInterval) {
      window.clearInterval(node.__vibeeCountdownInterval);
      node.__vibeeCountdownInterval = null;
    }

    var serverNowAttr = parseInt(node.getAttribute('data-server-now'), 10);
    var skewMs = 0;
    if (!isNaN(serverNowAttr)) {
      skewMs = serverNowAttr * 1000 - Date.now();
    }

    function tick() {
      var nowMs = Date.now() + skewMs;
      // Recomputed from the real clock every second → self-correcting,
      // never drifts and never sticks at 00:00:00.
      paint(node, getNextIstanbulMidnight(nowMs));
    }

    tick();
    node.__vibeeCountdownInterval = window.setInterval(tick, 1000);
  }

  function boot() {
    var nodes = document.querySelectorAll('[data-vibee-flash-countdown]');
    for (var i = 0; i < nodes.length; i += 1) {
      initNode(nodes[i]);
    }
  }

  // Stop intervals when the page is being unloaded/cached (bfcache safe).
  window.addEventListener('pagehide', function () {
    var nodes = document.querySelectorAll('[data-vibee-flash-countdown]');
    for (var i = 0; i < nodes.length; i += 1) {
      if (nodes[i].__vibeeCountdownInterval) {
        window.clearInterval(nodes[i].__vibeeCountdownInterval);
        nodes[i].__vibeeCountdownInterval = null;
      }
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init on Shopify theme-editor section load so the preview stays live.
  document.addEventListener('shopify:section:load', boot);
})();
